import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  Button,
  Chip,
  Paper,
  Skeleton,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { sessionService } from '../../services/session.service';
import { StudentSession } from '../../types/progress.types';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../context/NotificationContext';

dayjs.extend(relativeTime);

// ── Status Color System ──

const STATUS_COLORS = {
  live: { primary: '#e5484d', lightBg: '#fff0f0', border: '#fdd8d8' },
  inProgress: { primary: '#3b82f6', lightBg: '#eff6ff', border: '#bfdbfe' },
  upcoming: { primary: '#f59e0b', lightBg: '#fffbeb', border: '#fde68a' },
  completed: { primary: '#22c55e', lightBg: '#f0fdf4', border: '#bbf7d0' },
  done: { primary: '#7c3aed', lightBg: '#f5f3ff', border: '#ddd6fe' },
};

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: '#22c55e',
  intermediate: '#f59e0b',
  advanced: '#e5484d',
  expert: '#a855f7',
};

// ── Status Helpers ──

function getStatusGroup(session: StudentSession): 'live' | 'inProgress' | 'upcoming' | 'completed' | 'done' {
  if (session.status === 'active' && session.completedSteps < session.totalSteps) return 'live';
  if (session.status === 'active' && session.completedSteps >= session.totalSteps) return 'done';
  if (session.status === 'completed') return 'completed';
  if (session.status === 'scheduled') return 'upcoming';
  if (session.completedSteps > 0 && session.completedSteps < session.totalSteps) return 'inProgress';
  if (session.status === 'active' || session.status === 'preparing' || session.status === 'ready') return 'live';
  return 'upcoming';
}

function getStatusLabel(group: 'live' | 'inProgress' | 'upcoming' | 'completed' | 'done'): string {
  switch (group) {
    case 'live': return 'LIVE';
    case 'inProgress': return 'IN PROGRESS';
    case 'upcoming': return 'UPCOMING';
    case 'completed': return 'COMPLETED';
    case 'done': return 'DONE';
  }
}

// ── StepDots Component ──

function StepDots({ completed, total, color, size = 12 }: {
  completed: number;
  total: number;
  color: string;
  size?: number;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexWrap: 'nowrap' }}>
      {Array.from({ length: total }, (_, i) => {
        const isCompleted = i < completed;
        const isCurrent = i === completed && completed < total;
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <Box sx={{
                flex: '1 1 4px',
                maxWidth: 24,
                height: 2,
                bgcolor: isCompleted ? color : 'rgba(0,0,0,0.12)',
                borderRadius: 1,
              }} />
            )}
            <Box sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              bgcolor: isCompleted ? color : 'transparent',
              border: isCompleted ? 'none' : `2px solid ${isCurrent ? color : 'rgba(0,0,0,0.2)'}`,
              flexShrink: 0,
              position: 'relative',
              ...(isCurrent && {
                boxShadow: `0 0 0 3px ${color}30`,
                animation: 'dotPulse 2s ease-in-out infinite',
                '@keyframes dotPulse': {
                  '0%, 100%': { boxShadow: `0 0 0 3px ${color}30` },
                  '50%': { boxShadow: `0 0 0 6px ${color}15` },
                },
              }),
            }} />
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// ── Main Component ──

const StudentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;
  const [sessions, setSessions] = useState<StudentSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSessions = async () => {
    try {
      const data = await sessionService.getMySessionsAsStudent();
      setSessions(data);
    } catch {
      showNotificationRef.current('Failed to load sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const activeSession = sessions.find(
    (s) => s.status === 'active' && s.completedSteps > 0 && s.completedSteps < s.totalSteps,
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, fontFamily: '"DM Sans", sans-serif' }}>
        Welcome back, {user?.firstName || 'Student'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Continue your learning journey
      </Typography>

      {/* Active Session Strip */}
      {activeSession && (() => {
        const stripGroup = getStatusGroup(activeSession);
        const stripColors = STATUS_COLORS[stripGroup];
        return (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderLeft: `4px solid ${stripColors.primary}`,
              border: '1px solid',
              borderColor: 'divider',
              borderLeftColor: stripColors.primary,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'box-shadow 0.15s',
              '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: 15, color: '#1a1a2e', mb: 0.25 }}>
                {activeSession.workshopTitle}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.25 }}>
                {activeSession.name}
              </Typography>
              <StepDots
                completed={activeSession.completedSteps}
                total={activeSession.totalSteps}
                color={stripColors.primary}
                size={14}
              />
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                <Box component="span" sx={{ color: stripColors.primary, fontWeight: 600 }}>{activeSession.progressPercent}%</Box> complete &mdash; {activeSession.completedSteps} of {activeSession.totalSteps} steps
              </Typography>
            </Box>
            <Button
              variant="contained"
              endIcon={<ArrowIcon />}
              sx={{
                bgcolor: stripColors.primary,
                '&:hover': { bgcolor: stripGroup === 'live' ? '#c93c3f' : '#2563eb' },
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 600,
                flexShrink: 0,
                ml: 2,
              }}
              onClick={() => navigate(`/student/session/${activeSession.id}`)}
            >
              Continue Tech Eval
            </Button>
          </Paper>
        );
      })()}

      {/* Loading */}
      {loading && (
        <Grid container spacing={2}>
          {[0, 1, 2].map((i) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={i}>
              <Skeleton variant="rounded" height={320} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty state */}
      {!loading && sessions.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            You're not enrolled in any sessions yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your instructor will add you to a session.
          </Typography>
        </Box>
      )}

      {/* Session Cards */}
      {!loading && sessions.length > 0 && (
        <Grid container spacing={2}>
          {sessions.map((session) => {
            const statusGroup = getStatusGroup(session);
            const colors = STATUS_COLORS[statusGroup];
            const diffColor = DIFFICULTY_COLORS[session.workshopDifficulty || ''] || '#888';

            return (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={session.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'box-shadow 0.15s, border-color 0.15s, opacity 0.2s',
                    cursor: 'pointer',
                    '&:hover': { borderColor: '#d1d1d6', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' },
                    ...(statusGroup === 'completed' && { opacity: 0.8, '&:hover': { opacity: 1, borderColor: '#d1d1d6', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' } }),
                  }}
                >
                  {/* Card Header — status-tinted */}
                  <Box sx={{
                    p: 2,
                    bgcolor: colors.lightBg,
                    borderBottom: `1px solid ${colors.border}`,
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: 15, color: '#1a1a2e', flex: 1, mr: 1 }}>
                        {session.workshopTitle}
                      </Typography>
                      <Chip
                        label={getStatusLabel(statusGroup)}
                        size="small"
                        sx={{
                          bgcolor: colors.primary,
                          color: '#fff',
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: 10,
                          fontWeight: 600,
                          height: 22,
                          letterSpacing: '0.05em',
                          ...(statusGroup === 'live' && {
                            animation: 'chipPulse 2s ease-in-out infinite',
                            '@keyframes chipPulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.7 },
                            },
                          }),
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Card Body */}
                  <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Description */}
                    <Typography sx={{
                      fontSize: 13,
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      mb: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: 39,
                    }}>
                      {session.workshopDescription || 'No description available'}
                    </Typography>

                    {/* Completed cards — date + feedback indicator */}
                    {statusGroup === 'completed' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: 'text.secondary' }}>
                          {session.completedAt
                            ? `Completed on ${new Date(session.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}${session.timeMinutes ? ` · ${session.timeMinutes} min` : ''}`
                            : 'Finished'}
                        </Typography>
                        {session.feedbackSubmitted !== undefined && (
                          <Chip
                            label={session.feedbackSubmitted ? 'Feedback sent' : 'Feedback pending'}
                            size="small"
                            color={session.feedbackSubmitted ? 'success' : 'warning'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: 10, ml: 0.5 }}
                            onClick={!session.feedbackSubmitted ? (e) => {
                              e.stopPropagation();
                              navigate(`/student/session/${session.id}`);
                            } : undefined}
                          />
                        )}
                      </Box>
                    )}

                    {/* Upcoming cards — schedule */}
                    {statusGroup === 'upcoming' && session.scheduledStart && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: 'text.secondary' }}>
                          {dayjs(session.scheduledStart).fromNow()} &mdash; {dayjs(session.scheduledStart).format('MMM D, h:mm A')}
                        </Typography>
                      </Box>
                    )}

                    {/* Meta row: difficulty, steps, duration */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                      {session.workshopDifficulty && (
                        <Chip
                          label={session.workshopDifficulty}
                          size="small"
                          sx={{
                            bgcolor: `${diffColor}15`,
                            color: diffColor,
                            border: `1px solid ${diffColor}30`,
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: 10,
                            fontWeight: 500,
                            textTransform: 'capitalize',
                            height: 22,
                          }}
                        />
                      )}
                      <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12, color: 'text.secondary' }}>
                        {session.totalSteps} steps
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>|</Typography>
                      <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 12, color: 'text.secondary' }}>
                        {session.duration} min
                      </Typography>
                    </Box>

                    {/* Step Dots */}
                    <Box sx={{ mb: 0.5 }}>
                      <StepDots
                        completed={session.completedSteps}
                        total={session.totalSteps}
                        color={colors.primary}
                      />
                    </Box>
                    <Typography sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 11,
                      color: 'text.secondary',
                      mb: 'auto',
                    }}>
                      {statusGroup === 'upcoming' ? 'Not started' :
                        statusGroup === 'completed' ? `${session.completedSteps} of ${session.totalSteps} complete` :
                        `${session.progressPercent}% complete — ${session.completedSteps} of ${session.totalSteps} steps`}
                    </Typography>

                    {/* CTA Button */}
                    <Box sx={{ mt: 2 }}>
                      {statusGroup === 'live' && (
                        <Button
                          variant="contained"
                          fullWidth
                          endIcon={<ArrowIcon />}
                          sx={{ bgcolor: '#e5484d', '&:hover': { bgcolor: '#c93c3f' }, fontFamily: '"DM Sans", sans-serif', fontWeight: 600 }}
                          onClick={() => navigate(`/student/session/${session.id}`)}
                        >
                          {session.completedSteps > 0 ? 'Continue' : 'Join Now'}
                        </Button>
                      )}
                      {statusGroup === 'inProgress' && (
                        <Button
                          variant="contained"
                          fullWidth
                          endIcon={<ArrowIcon />}
                          sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, fontFamily: '"DM Sans", sans-serif', fontWeight: 600 }}
                          onClick={() => navigate(`/student/session/${session.id}`)}
                        >
                          Continue
                        </Button>
                      )}
                      {statusGroup === 'upcoming' && (
                        <Button
                          variant="outlined"
                          fullWidth
                          disabled
                          sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 500, color: 'text.secondary' }}
                        >
                          View Details
                        </Button>
                      )}
                      {statusGroup === 'done' && (
                        <Button
                          variant="contained"
                          fullWidth
                          endIcon={<ArrowIcon />}
                          sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, fontFamily: '"DM Sans", sans-serif', fontWeight: 600 }}
                          onClick={() => navigate(`/student/session/${session.id}`)}
                        >
                          Continue
                        </Button>
                      )}
                      {statusGroup === 'completed' && (
                        <Button
                          variant="outlined"
                          fullWidth
                          sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 500, color: 'text.secondary' }}
                          onClick={() => navigate(`/student/session/${session.id}`)}
                        >
                          Review
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default StudentDashboard;
